import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/Icon';
import {
  chatAvatarUrl, chatInitials, chatTitle, formatListDate, groupCollage, initialsOf,
  isGroupChat, messagePreview, sortChats,
} from '../chatUtils';
import { ONLINE_WINDOW_MS } from '../chatConstants';

// Аватар чата: коллаж из участников для группы, фото или инициалы для личного.
const ChatAvatar = ({ chat }) => {
  if (isGroupChat(chat)) {
    const members = groupCollage(chat, 3).filter((m) => m.avatar);
    if (members.length >= 2) {
      return (
        <span className={`chat-avatar chat-avatar--collage count-${Math.min(members.length, 3)}`}>
          {members.map((member) => (
            <img key={member.userId} src={member.avatar} alt="" />
          ))}
        </span>
      );
    }
    return <span className="chat-avatar chat-avatar--group">{chatInitials(chat)}</span>;
  }

  const avatar = chatAvatarUrl(chat);
  if (avatar) return <img className="chat-avatar chat-avatar--img" src={avatar} alt="" />;
  return <span className="chat-avatar">{chatInitials(chat) || initialsOf(chatTitle(chat))}</span>;
};

// Левая колонка: поиск + список чатов (закреплённые сверху).
const ChatList = ({
  chats, activeChatId, onSelect, onTogglePinned, onToggleMuted, locale, loading,
  activityByUser = {}, onNewChat, onNewGroup,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    const sorted = sortChats(chats);
    if (!term) return sorted;
    return sorted.filter((chat) => {
      const title = chatTitle(chat).toLowerCase();
      const preview = messagePreview(chat.lastMessage, chat).toLowerCase();
      return title.includes(term) || preview.includes(term);
    });
  }, [chats, query]);

  return (
    <aside className="chat-list">
      <div className="chat-list-head">
        <h2 className="chat-list-title">{t('chat.list.title')}</h2>
        <div className="chat-list-head-actions">
          <button
            type="button"
            className="chat-icon-btn"
            onClick={onNewGroup}
            title={t('chat.list.newGroup')}
          >
            <Icon name="users" size={18} />
          </button>
          <button
            type="button"
            className="chat-icon-btn"
            onClick={onNewChat}
            title={t('chat.list.newChat')}
          >
            <Icon name="plus" size={18} />
          </button>
        </div>
      </div>

      <div className="chat-search">
        <Icon name="search" size={16} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('chat.list.searchPlaceholder')}
        />
      </div>

      <div className="chat-list-scroll">
        {loading && chats.length === 0 && (
          <div className="chat-list-empty">
            <div className="strict-loader" />
          </div>
        )}

        {!loading && visible.length === 0 && (
          <div className="chat-list-empty">
            <Icon name="message-square" size={32} />
            <p>{query ? t('chat.list.notFound') : t('chat.list.empty')}</p>
            {!query && (
              <div className="chat-empty-actions">
                <button type="button" className="chat-new-chat-btn" onClick={onNewChat}>
                  <Icon name="plus" size={16} />
                  {t('chat.list.writeToEmployee')}
                </button>
                <button type="button" className="chat-new-chat-btn" onClick={onNewGroup}>
                  <Icon name="users" size={16} />
                  {t('chat.list.createGroup')}
                </button>
              </div>
            )}
          </div>
        )}

        {visible.map((chat) => (
          <button
            key={chat.id}
            type="button"
            className={`chat-list-item${chat.id === activeChatId ? ' active' : ''}`}
            onClick={() => onSelect(chat)}
          >
            <span className="chat-avatar-wrap">
              <ChatAvatar chat={chat} />
              {chat.peerId && Date.now() - (activityByUser[chat.peerId] || 0) < ONLINE_WINDOW_MS && (
                <span className="chat-online-dot" title={t('chat.window.activeNow')} />
              )}
            </span>

            <span className="chat-list-main">
              <span className="chat-list-row">
                <span className="chat-list-name">{chatTitle(chat)}</span>
                <span className="chat-list-date">
                  {formatListDate(chat.lastMessage?.createdAt, locale)}
                </span>
              </span>
              <span className="chat-list-row">
                <span className="chat-list-preview">{messagePreview(chat.lastMessage, chat)}</span>
                <span className="chat-list-marks">
                  {chat.pinned && (
                    <span
                      className="chat-mark"
                      role="button"
                      tabIndex={-1}
                      title={t('chat.list.unpin')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePinned(chat.id, false);
                      }}
                    >
                      <Icon name="tag" size={13} />
                    </span>
                  )}
                  {chat.muted && (
                    <span
                      className="chat-mark"
                      role="button"
                      tabIndex={-1}
                      title={t('chat.list.unmute')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleMuted(chat.id, false);
                      }}
                    >
                      <Icon name="slash" size={13} />
                    </span>
                  )}
                  {chat.unreadCount > 0 && (
                    <span className="chat-unread">{chat.unreadCount}</span>
                  )}
                </span>
              </span>
            </span>
          </button>
        ))}
      </div>
    </aside>
  );
};

export default ChatList;
